import React, { useState } from 'react';
import { Mail, MapPin, Loader2, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useForm } from 'react-hook-form';
import { SEO, contactPageJsonLd, breadcrumbs } from '../components/SEO';

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export const Contact: React.FC = () => {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ContactFormData>();

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setStatusMessage('');

    try {
      // Simple mailto fallback for now
      const mailtoLink = `mailto:contact@cemates.me?subject=${encodeURIComponent(data.subject)}&body=${encodeURIComponent(`Name: ${data.name}\nEmail: ${data.email}\n\n${data.message}`)}`;
      window.location.href = mailtoLink;
      
      setSubmitStatus('success');
      setStatusMessage(t('contact', 'successMessage'));
      reset();
    } catch (error) {
      setSubmitStatus('error');
      setStatusMessage(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-32 pb-20">
      <SEO 
        title="Contact"
        description="Get in touch with Hüseyin Cem Ateş. Contact for web development, mobile apps, or digital solutions inquiries."
        canonical="/contact"
        jsonLd={{ "@context": "https://schema.org", "@graph": [contactPageJsonLd, breadcrumbs.contact] }}
      />
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-semibold text-foreground mb-4">
            {t('contact', 'title')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            {t('contact', 'subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          {/* Contact Information */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-6">
                {t('contact', 'infoTitle')}
              </h2>

              <div className="space-y-6">
                <a 
                  href="mailto:contact@cemates.me" 
                  className="flex items-center gap-4 group"
                >
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center group-hover:bg-foreground/10 transition-colors">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('contact', 'email')}</p>
                    <p className="font-medium group-hover:text-primary transition-colors">contact@cemates.me</p>
                  </div>
                </a>

                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('contact', 'location')}</p>
                    <p className="font-medium">{t('contact', 'locationValue')}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-border">
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
                {t('contact', 'officeHours')}
              </h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('contact', 'mondayFriday')}</span>
                  <span className="font-medium">9:00 - 18:00 CET</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('contact', 'saturday')}</span>
                  <span className="font-medium">{t('contact', 'byAppointment')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('contact', 'sunday')}</span>
                  <span className="font-medium">{t('contact', 'closed')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-card border border-border rounded-2xl p-8">
              <h2 className="text-lg font-medium mb-6">{t('contact', 'sendTitle')}</h2>
              
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      {t('contact', 'nameLabel')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      {...register('name', { required: true })}
                      className={`w-full px-4 py-2.5 rounded-lg border text-sm ${errors.name ? 'border-destructive' : 'border-border'} focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-background transition-all`}
                      placeholder={t('contact', 'namePlaceholder') || 'Your name'}
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      {t('contact', 'emailLabel')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      {...register('email', {
                        required: true,
                        pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
                      })}
                      className={`w-full px-4 py-2.5 rounded-lg border text-sm ${errors.email ? 'border-destructive' : 'border-border'} focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-background transition-all`}
                      placeholder={t('contact', 'emailPlaceholder') || 'your@email.com'}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium mb-2">
                    {t('contact', 'subjectLabel')}
                  </label>
                  <input
                    type="text"
                    id="subject"
                    {...register('subject', { required: true })}
                    className={`w-full px-4 py-2.5 rounded-lg border text-sm ${errors.subject ? 'border-destructive' : 'border-border'} focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-background transition-all`}
                    placeholder={t('contact', 'subjectPlaceholder') || 'How can we help?'}
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-2">
                    {t('contact', 'messageLabel')}
                  </label>
                  <textarea
                    id="message"
                    {...register('message', { required: true })}
                    rows={5}
                    className={`w-full px-4 py-2.5 rounded-lg border text-sm ${errors.message ? 'border-destructive' : 'border-border'} focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-background transition-all resize-none`}
                    placeholder={t('contact', 'messagePlaceholder') || 'Tell us about your project...'}
                  />
                </div>

                {submitStatus !== 'idle' && (
                  <div className={`p-4 rounded-lg text-sm ${submitStatus === 'success' ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'}`}>
                    {statusMessage}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-foreground text-background py-3 rounded-lg text-sm font-medium hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>{t('contact', 'sending')}</span>
                    </>
                  ) : (
                    <>
                      <span>{t('contact', 'sendButton')}</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
